import { ConstantValue } from './../../common/constant-value';
import { SnapshotService } from './../../snapshot/snapshot.service';
import { PAY_COLOR, REC_COLOR } from './../../common/color-util';
import { AmChartsService, AmChart } from '@amcharts/amcharts3-angular';
import { CurrencyDisplayUtil } from './../../common/currency-display-util';
import {
    GetSurvivalHorizonServiceDataOutput,
    SurvivalHorizonData,
    AccountFlowBreakdown,
    ThroughputData
} from '@liquitics/transaction';
import { AppConfig } from './../../app.config';
import { DashboardEventService } from './../../dashboard/dashboard-event.service';
import { SurvivalHorizonService } from './../survival-horizon.service';
import { Subscription } from 'rxjs';
import { Component, OnInit, AfterViewInit, OnDestroy, Input } from '@angular/core';

const INSIGNIFICANT_RATIO_THRESHOLD = 0.05;

@Component({
    selector: 'app-throughput',
    templateUrl: './throughput.component.html',
    styleUrls: ['./throughput.component.scss']
})
export class ThroughputComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    title: string;
    loading = false;
    allPossibleAccounts: ConstantValue[];
    accounts: ConstantValue[] = [];
    selectedAccounts: ConstantValue[];

    accountChart: AmChart;
    deskChart: AmChart;
    productChart: AmChart;
    clientGroupChart: AmChart;
    throughput: ThroughputData;

    payColor: string;
    recColor: string;

    private dataStreamDisposable: Subscription;

    constructor(private survivalHorizonService: SurvivalHorizonService,
        private dashboardEventService: DashboardEventService,
        private amChartsService: AmChartsService,
        private snapshotService: SnapshotService,
        private appConfig: AppConfig) {
        this.payColor = PAY_COLOR;
        this.recColor = REC_COLOR;
    }

    ngOnInit() {
        this.allPossibleAccounts = ConstantValue.fromKeyValueObject(this.snapshotService.applicationConfiguration.accountNames);
    }

    ngAfterViewInit() {
        this.createBreakdownCharts();
        this.dataStreamDisposable = this.survivalHorizonService.getPollingDataSubject()
            .subscribe({
                next: (data: GetSurvivalHorizonServiceDataOutput) => {
                    if (data.accountFlowBreakdowns) {
                        this.updateBreakdownCharts(data.accountFlowBreakdowns);
                    }
                    setTimeout(() => {
                        this.throughput = data.throughput;
                    })
                }, error: (err) => {
                    console.log(err);
                    throw err;
                }
            });
    }

    ngOnDestroy() {
        if (this.dataStreamDisposable) {
            this.dataStreamDisposable.unsubscribe();
        }
    }

    createBreakdownCharts() {
        const colorStrip = [this.payColor, this.payColor, this.recColor, this.recColor];
        const valueFields = [['authorizedPayAmount', 'Authorized Pay Amount', 'actualPayAmount', 'Actual Pay Amount'],
        ['authorizedRecAmount', 'Authorized Receive Amount', 'actualRecAmount', 'Actual Receive Amount']];
        this.accountChart = createLayeredColumnChart(this.amChartsService, 'accountdiv', 'categoryName',
            valueFields, colorStrip);
        this.deskChart = createLayeredColumnChart(this.amChartsService, 'deskdiv', 'categoryName',
            valueFields, colorStrip);
        this.productChart = createLayeredColumnChart(this.amChartsService, 'productdiv', 'categoryName',
            valueFields, colorStrip);
        this.clientGroupChart = createLayeredColumnChart(this.amChartsService, 'clientgroupdiv', 'categoryName',
            valueFields, colorStrip);
    }

    updateBreakdownCharts(accountFlowBreakdowns: AccountFlowBreakdown[]) {
        const accountNames = this.snapshotService.applicationConfiguration.accountNames;
        // const existingAccountNames = [];
        const dataByType = accountFlowBreakdowns.reduce((grouped, d) => {
            const key = d.categoryType;
            if (key === 'ACCOUNT') {
                const accountName = d.categoryName;
                if (this.accounts.findIndex((a: ConstantValue) => { return a.objectValue === accountName; }) === -1) {
                    const found = this.allPossibleAccounts.find((a: ConstantValue) => { return a.objectValue === accountName; });
                    if (found) {
                        this.accounts.push(found);
                    }
                }
                d.categoryName = accountNames[accountName];
            }
            grouped[key] = grouped[key] || [];
            grouped[key].push(d);
            return grouped;
        }, {});
        this.accountChart.dataProvider = this.createColumnData(dataByType['ACCOUNT']);
        this.accountChart.validateData();
        this.accountChart.invalidateSize();
        this.deskChart.dataProvider = this.createColumnData(dataByType['DESK']);
        this.deskChart.validateData();
        this.deskChart.invalidateSize();
        this.productChart.dataProvider = this.createColumnData(dataByType['PRODUCT']);
        this.productChart.validateData();
        this.productChart.invalidateSize();
        this.clientGroupChart.dataProvider = this.createColumnData(dataByType['CLIENT_GROUP']);
        this.clientGroupChart.validateData();
        this.clientGroupChart.invalidateSize();
        this.loading = false;
    }

    createColumnData(accountFlowBreakdowns: AccountFlowBreakdown[]): ThroughputRow[] {
        const filteredRows: ThroughputRow[] = [];
        if (accountFlowBreakdowns) {
            const dataByCategoryName = accountFlowBreakdowns.reduce((grouped, d) => {
                const key = d.categoryName;
                grouped[key] = grouped[key] || [];
                grouped[key].push(d);
                return grouped;
            }, {});
            let authorizedPayTotal = 0;
            let authorizedRecTotal = 0;
            const rows: ThroughputRow[] = [];
            Object.keys(dataByCategoryName)
                .forEach((key: string) => {
                    const rowData: ThroughputRow = dataByCategoryName[key]
                        .reduce((reduced: ThroughputRow, curr: AccountFlowBreakdown) => {
                            if (curr.direction === 'PAY') {
                                if (curr.accountFlowType === 'AUTHORIZED' || curr.accountFlowType === 'AUTHORIZED_ACTUAL') {
                                    reduced.authorizedPayAmount += curr.amount;
                                }
                                if (curr.accountFlowType === 'ACTUAL' || curr.accountFlowType === 'AUTHORIZED_ACTUAL') {
                                    reduced.actualPayAmount += curr.amount;
                                }
                            } else {
                                if (curr.accountFlowType === 'AUTHORIZED' || curr.accountFlowType === 'AUTHORIZED_ACTUAL') {
                                    reduced.authorizedRecAmount += curr.amount;
                                }
                                if (curr.accountFlowType === 'ACTUAL' || curr.accountFlowType === 'AUTHORIZED_ACTUAL') {
                                    reduced.actualRecAmount += curr.amount;
                                }
                            }
                            reduced.categoryName = curr.categoryName;
                            authorizedPayTotal += Math.abs(reduced.authorizedPayAmount);
                            authorizedRecTotal += Math.abs(reduced.authorizedRecAmount);
                            return reduced;
                        }, new ThroughputRow());
                    rowData.authorizedPayAmount = Math.abs(rowData.authorizedPayAmount);
                    rowData.actualPayAmount = Math.abs(rowData.actualPayAmount);
                    rowData.authorizedRecAmount = Math.abs(rowData.authorizedRecAmount);
                    rowData.actualRecAmount = Math.abs(rowData.actualRecAmount);
                    rows.push(rowData);
                });
            const otherRowData = new ThroughputRow();
            otherRowData.categoryName = 'Other';

            rows.forEach((row: ThroughputRow) => {
                if ((row.authorizedPayAmount / authorizedPayTotal) > INSIGNIFICANT_RATIO_THRESHOLD) {
                    filteredRows.push(row);
                } else {
                    otherRowData.authorizedPayAmount += row.authorizedPayAmount;
                    otherRowData.authorizedRecAmount += row.authorizedRecAmount;
                    otherRowData.actualPayAmount += row.actualPayAmount;
                    otherRowData.actualRecAmount += row.actualRecAmount;
                }
            })
            filteredRows.push(otherRowData);
        }
        return filteredRows;
    }

    accountsSelected(event) {
        this.survivalHorizonService.setSelectedAccounts(this.selectedAccounts.map((cv) => { return cv.objectValue; }));
        this.loading = true;
    }

    clearAccountFilter() {
        this.selectedAccounts = [];
        this.accountsSelected(null);
    }

    formatAmount(value: number) {
        return CurrencyDisplayUtil.toShortNumber(value, 2);
    }
}

function createLayeredColumnChart(amChartsService: AmChartsService,
    divId: string,
    labelField: string,
    valueFields: string[][],
    colors: string[]): AmChart {

    const graphs = [];
    valueFields.forEach((field: string[]) => {
        const authorizedGraph = {
            'balloonFunction': (item) => {
                return `${field[1]}: ${CurrencyDisplayUtil.toShortNumber(item.values.value, 2)}`;
            },
            'labelFunction': (item) => {
                return item.graph.data.length <= 6 ? CurrencyDisplayUtil.toShortNumber(item.values.value, 2) : '';
            },
            'labelText': '[[value]]',
            'fillAlphas': 0.2,
            'lineAlpha': 0.9,
            // 'title': 'Authorized',
            'type': 'column',
            // 'clustered': true,
            // 'columnWidth': 0.5,
            'valueField': field[0],
            // 'labelText': `[[${field[1]}]]`,
            'dashLength': 2,
            'lineThickness': 2,
        };
        const actualGraph = {
            'balloonFunction': (item) => {
                return `${field[3]}: ${CurrencyDisplayUtil.toShortNumber(item.values.value, 2)}`;
            },
            'labelFunction': (item) => {
                return item.graph.data.length <= 6 ? CurrencyDisplayUtil.toShortNumber(item.values.value, 2) : '';
            },
            'labelText': '[[value]]',
            'fillAlphas': 0.9,
            'lineAlpha': 0.2,
            // 'title': 'Actual',
            'type': 'column',
            // 'clustered': false,
            // 'columnWidth': 0.25,
            'valueField': field[2],
            // 'labelText': `[[${field[3]}]]`,
        };
        graphs.push(authorizedGraph);
        graphs.push(actualGraph);
    })

    const chart = amChartsService.makeChart(divId, {
        'theme': 'light',
        'type': 'serial',
        'hideCredits': true,
        'valueAxes': [{
            // 'position': 'left',
        }],
        'startDuration': 0,
        'graphs': graphs,
        'colors': colors,
        'plotAreaFillAlphas': 0.1,
        'categoryField': labelField,
        'categoryAxis': {
            'gridPosition': 'start',
            'autoWrap': true

        },
    })
    return chart;
}

class ThroughputRow {
    categoryName: string;
    bullet: string;
    authorizedPayAmount = 0;
    actualPayAmount = 0;
    authorizedRecAmount = 0;
    actualRecAmount = 0
    authorizedPayAmountBullet = 'Authorized(Pay)';
    actualPayAmountBullet = 'Actual(Pay)';
    authorizedRecAmountBullet = 'Authorized(Rec)';
    actualRecAmountBullet = 'Actual(Rec)';
}

